import {
  STATUS_PAGE_STATES,
  STATUS_TYPES,
  LAUNCH_TYPES,
} from '../AirAmbulanceForm/constants';
import { checkLaunchType } from '../AirAmbulanceForm/utils';
import type { StatusPageState, TripData, StatusItem } from '../types';

/**
 * Determines the current status state of an air ambulance trip based on trip data and status history.
 *
 * @param tripData - The trip data object containing information about the air ambulance trip
 * @param statuses - Array of status objects representing the status history of the trip
 * @param isTimeout - If the request timed out
 * @returns The determined status state, which can be one of:
 *   - STATUS_PAGE_STATES.TEST if the trip is in test mode
 *   - STATUS_PAGE_STATES.CANCELLED if statusCode is "408"
 *   - The page state corresponding to the latest status type if available
 *   - STATUS_PAGE_STATES.SUBMITTED as a fallback
 */
export const getStatusState = (
  tripData: TripData | null | undefined,
  statuses: StatusItem[] | null | undefined,
  isTimeout: boolean,
): StatusPageState => {
  const launchType = checkLaunchType(tripData?.launch_type ?? null);

  // Find the latest status with a valid type, not just the last status
  let latestValidStatus: StatusItem | null = null;
  if (statuses?.length && statuses.length > 0) {
    // Look backwards through statuses to find the most recent one with a type
    for (let i = statuses.length - 1; i >= 0; i--) {
      if (statuses[i]?.type) {
        latestValidStatus = statuses[i];
        break;
      }
    }
  }

  const latestStatusType = latestValidStatus?.type;

  if (launchType === LAUNCH_TYPES.TEST) {
    return STATUS_PAGE_STATES.TEST;
  }

  if (isTimeout) {
    return STATUS_PAGE_STATES.CANCELLED;
  }

  // Simple logic: if we have a valid status type, use it;
  // if we have any statuses but no type, assume ACCEPTED to prevent flickering;
  // otherwise SUBMITTED
  const hasAnyStatuses = statuses && statuses.length > 0;
  const hasValidType =
    latestStatusType && STATUS_TYPES[latestStatusType] !== undefined;

  const result = hasValidType
    ? STATUS_TYPES[latestStatusType].pageState
    : hasAnyStatuses
      ? STATUS_PAGE_STATES.ACCEPTED // Default to ACCEPTED if we have statuses but no type
      : STATUS_PAGE_STATES.SUBMITTED; // Only SUBMITTED if no statuses at all

  return result;
};
